import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ExternalLink } from 'lucide-react';
import type { PollAPI } from '@shared/types/api';
import { useT } from '@/lib/i18n';

interface Props {
  poll: PollAPI.UserPoll;
  onSelect: (answerId: string) => void;
  isPending: boolean;
}

/**
 * Poll options as cards: image, title, subtitle, tags, a "More about it" link
 * and a short description, plus vote counts once results are visible. After
 * voting the cards lock until the user taps "Change my vote".
 */
export function PollOptionCards({ poll, onSelect, isPending }: Props) {
  const { t } = useT();
  const [changing, setChanging] = useState(false);
  const canSelect = !poll.hasResponded || changing;
  const showResults = poll.publicResults && !!poll.results;
  const topCount = Math.max(0, ...(poll.results ?? []).map((r) => r.count));

  const select = (answerId: string) => {
    if (!canSelect || isPending) return;
    setChanging(false);
    onSelect(answerId);
  };

  return (
    <div className="space-y-3">
      {poll.answers.map((answer, i) => {
        const result = poll.results?.find((r) => r.answerId === answer.id);
        const isUserVote = poll.userResponse === answer.id;
        const isLeading = showResults && !!result && result.count > 0 && result.count === topCount;

        return (
          <motion.div
            key={answer.id}
            role={canSelect ? 'button' : undefined}
            tabIndex={canSelect ? 0 : undefined}
            onClick={() => select(answer.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                select(answer.id);
              }
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className={`rounded-2xl border p-3 space-y-3 transition-all ${
              isUserVote
                ? 'border-primary-500 ring-1 ring-primary-500/60'
                : 'border-type-200 dark:border-neutral-800'
            } ${canSelect ? 'cursor-pointer hover:border-primary-500/60' : ''} ${isPending ? 'opacity-60' : ''}`}
          >
            <div className="flex gap-3">
              {answer.imageUrl && (
                <img src={answer.imageUrl} alt="" className="w-20 sm:w-24 aspect-square object-contain rounded-lg bg-neutral-100 dark:bg-neutral-800 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="font-bold leading-snug">{answer.text}</div>
                {answer.subtitle && <div className="text-xs text-dark-muted">{answer.subtitle}</div>}
                {!!answer.tags?.length && (
                  <div className="flex flex-wrap gap-1">
                    {answer.tags.map((tag, ti) => (
                      <span
                        key={tag}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          ti === 0 ? 'bg-primary-500/20 text-primary-500' : 'bg-neutral-200 dark:bg-neutral-800 text-dark-muted'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {answer.linkUrl && (
                    <a
                      href={answer.linkUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-dark-muted hover:text-primary-500"
                    >
                      <ExternalLink className="w-3 h-3" /> {t('polls.moreAbout')}
                    </a>
                  )}
                  {isUserVote && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-white text-[11px] font-medium">
                      <Check className="w-3 h-3" /> {t('polls.yourVote')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {answer.description && <p className="text-sm text-dark-muted">“{answer.description}”</p>}

            {showResults && result && (
              <div className="space-y-1">
                <div className="text-xs font-medium">
                  {t('polls.voted', { count: result.count })}
                  {isLeading && <span className="text-primary-500"> · {t('polls.leading')}</span>}
                </div>
                <div className="h-1.5 w-24 bg-dark-border rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${result.percentage}%` }}
                    transition={{ duration: 0.5 }}
                    className={`h-full rounded-full ${isUserVote ? 'bg-primary-500' : 'bg-primary-500/50'}`}
                  />
                </div>
              </div>
            )}
          </motion.div>
        );
      })}

      {poll.hasResponded && (
        <button
          type="button"
          onClick={() => setChanging((v) => !v)}
          className="btn btn-outline btn-sm rounded-full"
        >
          {changing ? t('polls.keepVote') : t('polls.changeVote')}
        </button>
      )}
    </div>
  );
}
